// Shared vuelidate rule fragments for the dashboard's student create (add_student.vue) and
// edit (userEdit.vue) forms, mirroring the same rules used by the public reservation form
// (web/src/views/reservation.vue) and enforced again server-side by
// backend/app/Models/RegistrationFormSettings.php — so all three surfaces never drift apart.

export function toEnglishDigitsStr(value) {
  if (!value) return value;
  return String(value).replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));
}

// Value is trimmed only for length purposes here; the actual field value sent to
// the backend is trimmed separately before submit.
export const trimmedMinLength = (min) => (value) => !value || String(value).trim().length >= min;

// Arabic letters (matches backend RegistrationFormSettings::ARABIC_NAME_REGEX) + spaces only —
// no Latin letters, no digits (Western or Arabic-Indic), no symbols.
export const arabicOnly = (value) => !value || /^[ء-يـ\s]+$/.test(value);

export const exactDigits = (len) => (value) => {
  if (!value) return true;
  const digits = toEnglishDigitsStr(value);
  return /^\d+$/.test(digits) && digits.length === len;
};

export const startsWithZeroOne = (value) => {
  if (!value) return true;
  return toEnglishDigitsStr(String(value)).startsWith('01');
};

// parent_phone accepts either an Egyptian mobile or landline number — unlike
// exactDigits/startsWithZeroOne (student_phone/whatsapp_number: always mobile,
// exactly 11 digits, must start with "01"), only the digit-count range matters
// here. Mirrors the backend's digits_between:7,11 rule.
export const digitsBetween = (min, max) => (value) => {
  if (!value) return true;
  const digits = toEnglishDigitsStr(value);
  return /^\d+$/.test(digits) && digits.length >= min && digits.length <= max;
};

export const isNumericOrEmpty = (value) => {
  if (value === null || value === undefined || value === '') return true;
  return /^\d+(\.\d+)?$/.test(toEnglishDigitsStr(String(value)));
};

export const minValueOrEmpty = (min) => (value) => {
  if (value === null || value === undefined || value === '') return true;
  if (!isNumericOrEmpty(value)) return true; // let the numeric validator own that failure
  return Number(toEnglishDigitsStr(value)) >= min;
};

export const maxValueOrEmpty = (max) => (value) => {
  if (value === null || value === undefined || value === '') return true;
  if (!isNumericOrEmpty(value)) return true; // let the numeric validator own that failure
  return Number(toEnglishDigitsStr(value)) <= max;
};

// Cross-field: student_actual_grade must not exceed maximum_grade. Reads the sibling
// field live via the getter so it stays reactive to changes on either field.
export const notGreaterThan = (getOtherValue) => (value) => {
  if (value === null || value === undefined || value === '') return true;
  const other = getOtherValue();
  if (other === null || other === undefined || other === '' || !isNumericOrEmpty(other)) return true;
  if (!isNumericOrEmpty(value)) return true;
  return Number(toEnglishDigitsStr(value)) <= Number(toEnglishDigitsStr(other));
};

export function toEnglishDigits(str) {
  return str ? String(str).replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d)) : str;
}

// Fallback only, used if /registration-settings can't be reached: mirrors
// RegistrationFormSettings::DEFAULTS + FIELD_META on the backend, which is the
// real source of truth shared with the public reservation form.
export const DEFAULT_FORM_SETTINGS = {
  address: { visible: true, required: false, label: 'العنوان', placeholder: 'المدينة / الحي', error_required: 'برجاء إدخال العنوان' },
  certificate_image: { visible: true, required: false, label: 'صورة الشهادة', upload_text: 'اضغط لرفع صورة الشهادة', upload_hint: 'PNG, JPG, JPEG, GIF (حتى 50MB)', error_required: 'برجاء رفع صورة الشهادة' },
  whatsapp_number: { visible: true, required: false, label: 'رقم الواتساب', placeholder: '01XXXXXXXXX', error_required: 'برجاء إدخال رقم الواتساب', error_digits: 'رقم الهاتف يجب أن يتكون من 11 رقم.', error_prefix: 'يجب أن يبدأ الرقم بـ 01.', validation: { type: 'mobile_phone', length: 11, prefix: '01' } },
  student_actual_grade: { visible: true, required: false, label: 'درجة الطالب (ماده الرياضيات)', placeholder: 'مثال: 85', error_required: 'برجاء إدخال درجة الطالب (ماده الرياضيات)', error_invalid: 'يرجى إدخال أرقام صحيحة.', error_min: 'درجة الطالب يجب أن تكون صفر أو أكبر.', error_max: 'درجة الطالب غير صحيحة.', error_range: 'درجة الطالب يجب أن تكون أقل من أو تساوي الدرجة العظمى.' },
  maximum_grade: { visible: true, required: false, label: 'الدرجة العظمى (ماده الرياضيات)', placeholder: 'مثال: 100', error_required: 'برجاء إدخال الدرجة العظمى (ماده الرياضيات)', error_invalid: 'يرجى إدخال أرقام صحيحة.', error_min: 'الدرجة العظمى غير صحيحة.', error_max: 'الدرجة العظمى لا يمكن أن تزيد عن 120.' },
  student_type: { visible: true, required: false, label: 'نوع الطالب', error_required: 'برجاء اختيار نوع الطالب' },
  // required: true mirrors RegistrationFormSettings::DEFAULTS['education_type'] on the
  // backend — the one field that's required by default. options is empty here (this
  // fallback only applies when /registration-settings couldn't be reached at all, so
  // there's no academic year to source real options from).
  education_type: { visible: true, required: true, label: 'نظام التعليم', error_required: 'برجاء اختيار نظام التعليم', options: [] },
};
